import type { MarketplaceListingSort } from '../browse/__generated__/marketplace-browse.zeus';

/**
 * URL-facing sort slugs. These are a stable public contract: they appear in
 * shareable region-page URLs, so renaming one breaks existing links.
 */
const LISTING_SORT_SLUGS = ['newest', 'price-asc', 'price-desc'] as const;

export type ListingSortSlug = (typeof LISTING_SORT_SLUGS)[number];

export const DEFAULT_LISTING_SORT_SLUG: ListingSortSlug = 'newest';

const LISTING_SORT_SLUG_SET = new Set<string>(LISTING_SORT_SLUGS);

export const isListingSortSlug = (value: string): value is ListingSortSlug =>
  LISTING_SORT_SLUG_SET.has(value);

const LISTING_SORT_LABELS = {
  newest: 'Newest',
  'price-asc': 'Price: low to high',
  'price-desc': 'Price: high to low',
} as const satisfies Record<ListingSortSlug, string>;

export const listingSortOptions: { value: ListingSortSlug; label: string }[] =
  LISTING_SORT_SLUGS.map((slug) => ({ value: slug, label: LISTING_SORT_LABELS[slug] }));

/** Graph sort keys each slug requests. */
const LISTING_SORT_KEYS = {
  newest: ['LIST_DATE_DESC'],
  'price-asc': ['LIST_PRICE_ASC', 'LIST_DATE_DESC'],
  'price-desc': ['LIST_PRICE_DESC', 'LIST_DATE_DESC'],
} as const satisfies Record<ListingSortSlug, readonly MarketplaceListingSort[]>;

/** Resolves a slug to graph sort keys, falling back to the default slug. */
export const listingSortKeys = (
  slug: string | null | undefined,
): readonly MarketplaceListingSort[] =>
  LISTING_SORT_KEYS[slug != null && isListingSortSlug(slug) ? slug : DEFAULT_LISTING_SORT_SLUG];
